import { ENEMIES } from '../../data/enemies';
import { GROUND_Y, SIM_HZ } from '../../data/tuning';
import { createCircle, type Circle } from '../collision';

/**
 * EN-41 Águila real (GDD §8.3, issue #127). A golden eagle that dives from above towards the height the pilot had
 * when the warning started, announced by a screech and a ground shadow 1.0 s ahead (GDD §8.1, pillar 2). It enters
 * from above, so it is not an INTERCEPT case: its own warning is the screech and the shadow. Nobody dies and
 * nothing is destroyed (GDD §3.4): if the dive line would reach the ground, the eagle levels off and leaves the
 * screen to the left. Pure and deterministic: the dive line is computed from the ticks since the dive started.
 */

export const EN41 = ENEMIES['EN-41'];

export const WARNING_TICKS = Math.round(EN41.warningSeconds * SIM_HZ);
/** The lowest altitude the eagle allows itself before levelling off (never touches the ground, §3.4). */
export const LEVELLED_Y = GROUND_Y + 0.5;

export interface GoldenEagleState {
  /** Tick the warning started, or null before the trigger line is crossed. */
  warningTick: number | null;
  /** Target of the dive: the pilot's position locked when the warning started. */
  target: { x: number; y: number } | null;
  /** The point the eagle dives from (above the ceiling, ahead of the pilot). */
  start: { x: number; y: number } | null;
  /** Ticks since the dive started (0 while the warning is on). */
  diveTicks: number;
  /** Phases of the eagle: waiting, diving, levelled (leaving) or gone. */
  phase: 'waiting' | 'diving' | 'levelled' | 'gone';
}

export const createGoldenEagle = (): GoldenEagleState => ({ warningTick: null, target: null, start: null, diveTicks: 0, phase: 'waiting' });

/**
 * The plane crossed the trigger line: lock the target at the pilot's position and place the eagle above the
 * ceiling ahead of it. Returns true when the warning just started (the runner emits the screech + shadow event).
 */
export function startWarning(state: GoldenEagleState, tick: number, plane: { x: number; y: number }): boolean {
  if (state.warningTick !== null) return false;
  state.warningTick = tick;
  state.target = { x: plane.x, y: plane.y };
  state.start = { x: plane.x + EN41.spawnAhead, y: EN41.spawnAbove };
  return true;
}

/**
 * One tick: after `WARNING_TICKS` the dive begins. During the warning the eagle is not in the playfield. Returns
 * true on the tick the dive starts.
 */
export function stepEagle(state: GoldenEagleState, tick: number): boolean {
  if (state.phase !== 'waiting' || state.warningTick === null) return false;
  if (tick - state.warningTick < WARNING_TICKS) return false;
  state.phase = 'diving';
  return true;
}

/**
 * Position of the eagle during its dive, from the ticks since it started, relative to the pilot's current x (the
 * plane moves with the level, so the eagle tracks it). The eagle starts `spawnAhead` u ahead of and above the
 * pilot and closes both the horizontal offset and the height over `diveSeconds` (so it meets the pilot's line),
 * aiming at the locked height. Past the target it keeps going down-left until it reaches `LEVELLED_Y`, then levels
 * off and leaves to the left at `leaveSpeed`. Returns null while it is only warning.
 */
export function eaglePosition(state: GoldenEagleState, playerX: number): { x: number; y: number } | null {
  if (state.phase !== 'diving' && state.phase !== 'levelled') return null;
  if (!state.start || !state.target) return null;
  const dt = state.diveTicks / SIM_HZ;
  const diveSeconds = (state.start.y - state.target.y) / EN41.diveSpeed;
  if (dt <= diveSeconds) {
    const progress = dt / diveSeconds;
    const x = playerX + EN41.spawnAhead * (1 - progress);
    const y = state.start.y + (state.target.y - state.start.y) * progress;
    return { x, y };
  }
  // Past the target: it keeps going left and down until it levels off.
  const extra = EN41.leaveSpeed * (dt - diveSeconds);
  const y = Math.max(LEVELLED_Y, state.target.y - extra);
  return { x: playerX - extra, y };
}

/** The eagle body: a circle of `hitboxRadius` u (70-80 % of the model, GDD §8.1). */
export const eagleCircle = (at: { x: number; y: number }): Circle => createCircle(at.x, at.y, EN41.hitboxRadius);
